<template>
  <div class="flex flex-col md:flex-row gap-4 px-2">
    <div class="filter-column filter-column--calendar md:pt-2">
      <div class="label">{{ $t('search.filters.calendarLabel') }}</div>
      <FilterItemDate
        :selected-dates="props.dates"
        @updated="
          ($event) => {
            $emit('updated', {
              dates: $event,
            })
          }
        "
      />
    </div>
    <div
      class="filter-column filter-column--options md:pt-2 flex flex-col [&>div]:flex [&>div]:justify-between gap-4"
    >
      <div class="flex flex-col">
        <div class="label">{{ $t('search.filters.classType') }}</div>
        <div class="mt-1 flex flex-wrap gap-1">
          <Chip
            v-for="item in otherFilters"
            :key="`kft-search-language-${item.key}`"
            :label="item.label"
            :selected="typeOfClass === item.key"
            class="chip-filter"
            @click="clickOtherFilter(item.key)"
          >
            {{ item.label }}
          </Chip>
        </div>
      </div>

      <div class="flex flex-col">
        <div class="label">{{ $t('search.selectFiltersComponent.price') }}</div>
        <div class="w-full mt-3 mx-0 price-slider">
          <Slider
            ref="sliderRange"
            v-model="price"
            :max="PRICE_MAX"
            :lazy="false"
            :format="format"
            :tooltips="false"
            :classes="sliderClasses"
            :options="sliderOptions"
            @change="
              (e: [number, number]) =>
                $emit('updated', { priceMin: e[0], priceMax: e[1] })
            "
          />
          <div class="grid grid-cols-2 gap-8">
            <div class="w-3/4">
              <input
                v-model="priceMinDisplay"
                type="text"
                class="border rounded-md py-0 px-4 text-center mx-0 mt-4 w-full h-8"
                @focus="handlePriceMinFocus"
                @blur="handlePriceMinBlur"
              />
            </div>
            <div class="w-3/4 ml-auto">
              <input
                v-model="priceMaxDisplay"
                type="text"
                class="border rounded-md py-0 px-4 text-center mx-0 mt-4 w-full h-8"
                @focus="handlePriceMaxFocus"
                @blur="handlePriceMaxBlur"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col">
        <div class="label">
          {{
            $t('search.selectFiltersComponent.showEventsInPreferredLanguage')
          }}
        </div>
        <div v-if="availableLanguages" class="gap-1 flex flex-wrap mt-1">
          <Chip
            v-for="(language, code) in availableLanguages"
            :key="`kft-search-language-${code}`"
            :label="language"
            :selected="languages.includes(code)"
            class="chip-filter"
            @click="$emit('updated', { languages: code })"
            >{{ language }}
          </Chip>
          <!-- @pressed="fnToggleFacet('languages', code)" -->
          <!-- :value="temporaryFilters.languages.indexOf(code) !== -1" -->
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// eslint-disable-next-line import/default
import Slider from '@vueform/slider'
import { fnGetAvailableLanguages } from '~/utils/localeHelpers'
import { formatMoney } from '~/utils/currencyHelpers'
import {
  parsePriceInput,
  parsePriceToNumber,
  processPriceMinInput,
  processPriceMaxInput,
} from '~/utils/priceInputHelpers'

const PRICE_MIN = 0
const PRICE_MAX = 1200

const { t } = useI18n()
const sliderRange = ref<InstanceType<typeof Slider> | null>(null)
const isPriceMinFocused = ref(false)
const isPriceMaxFocused = ref(false)
// Store the price value at focus time to detect if user actually typed
const priceMinAtFocus = ref<number | null>(null)
const priceMaxAtFocus = ref<number | null>(null)

onMounted(() => {
  if (sliderRange.value?.$el) {
    sliderRange.value.$el
      .querySelectorAll('.tooltip-text')
      .forEach((item: Element, index: number) => {
        item.classList.add('tooltip-text--' + (index + 1))
      })
  }
})

const props = withDefaults(
  defineProps<{
    dates?: []
    hasAvailableDates?: boolean
    languages?: string[]
    priceMin?: number
    priceMax?: number
    typeOfClass?: string
  }>(),
  {
    dates: () => [],
    hasAvailableDates: false,
    languages: () => [],
    priceMin: PRICE_MIN,
    priceMax: PRICE_MAX,
    typeOfClass: undefined,
  }
)

const format = (value: number) => formatMoney(value)

const sliderOptions = {
  range: {
    min: [PRICE_MIN],
    '10%': [20, 1],
    '60%': [150, 1],
    '80%': [900, 1],
    max: [PRICE_MAX],
  },
}

const sliderClasses = {
  target:
    'relative box-border select-none touch-none tap-highlight-transparent touch-callout-none disabled:cursor-not-allowed',
  focused: 'slider-focused',
  tooltipFocus: 'slider-tooltip-focus',
  tooltipDrag: 'slider-tooltip-drag',
  horizontal: 'slider-horizontal h-1.5',
  base: 'w-full h-full relative z-1 bg-gray-300 rounded',
  connects: 'w-full h-full relative overflow-hidden z-0 rounded',
  connect:
    'absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full bg-teal-900 cursor-pointer tap:duration-300 tap:transition-transform disabled:bg-gray-400 disabled:cursor-not-allowed',
  origin:
    'slider-origin absolute z-1 top-0 right-0 transform-origin-0 transform-style-flat h-full w-full h:h-0 v:-top-full txt-rtl-h:left-0 txt-rtl-h:right-auto v:w-0 tap:duration-300 tap:transition-transform',
  handle:
    'bg-teal-50 border border-teal-900 absolute rounded-full cursor-grab focus:outline-none h:w-[13px] h:h-[13px] h:-top-[4px] h:-right-[6px] txt-rtl-h:-left-[4px] txt-rtl-h:right-auto v:w-[13px] v:h-[13px] v:-top-[4px] v:-right-[4px] disabled:cursor-not-allowed focus:ring focus:ring-primary-active focus:ring-opacity-30',
  touchArea: 'h-full w-full',
  tooltip:
    'tooltip-text absolute block text-sm text-center font-semibold whitespace-nowrap text-gray-600',
  tooltipTop: 'bottom-4 merge-h:bottom-3',
  tooltipHidden: 'slider-tooltip-hidden',
  active: 'slider-active shadow-slider-active cursor-grabbing',
  draggable: 'cursor-ew-resize v:cursor-ns-resize',
  tap: 'slider-state-tap',
  drag: 'slider-state-drag',
}

const emit = defineEmits(['updated'])

const typeOfClass = ref(props.typeOfClass)
const otherFilters = computed(() => [
  {
    key: 'publicEvents',
    label: t('search.filters.publicTickets'),
  },
  {
    key: 'directEvents',
    label: t('search.filters.privateGroups'),
  },
  {
    key: 'online',
    label: t('search.filters.onlineEvents'),
  },
])

watch(
  () => props.priceMin,
  (newVal) => {
    if (newVal !== undefined) {
      price.value[0] = newVal
      // Only update input if not currently focused (to avoid interrupting user typing)
      if (!isPriceMinFocused.value) {
        priceMinInput.value = String(newVal)
      }
    }
  }
)

watch(
  () => props.priceMax,
  (newVal) => {
    if (newVal !== undefined) {
      price.value[1] = newVal
      // Only update input if not currently focused (to avoid interrupting user typing)
      if (!isPriceMaxFocused.value) {
        priceMaxInput.value = String(newVal)
      }
    }
  }
)

const clickOtherFilter = (key: string) => {
  typeOfClass.value = typeOfClass.value === key ? undefined : key
  emit('updated', { typeOfClass: typeOfClass.value })
}

const handlePriceMinFocus = (e: FocusEvent) => {
  isPriceMinFocused.value = true
  // Store the current price value at focus time to detect if user actually typed
  priceMinAtFocus.value = price.value[0]
  // Initialize input value with current price value
  priceMinInput.value = String(price.value[0])
  const target = e.target as HTMLInputElement
  // Position cursor at the end of the numeric value after focus
  // Use setTimeout to ensure DOM is updated after computed value changes
  setTimeout(() => {
    const numericValue = priceMinInput.value
    if (target && target.setSelectionRange) {
      target.setSelectionRange(numericValue.length, numericValue.length)
    }
  }, 0)
}

const handlePriceMaxFocus = (e: FocusEvent) => {
  isPriceMaxFocused.value = true
  // Store the current price value at focus time to detect if user actually typed
  priceMaxAtFocus.value = price.value[1]
  // Initialize input value with current price value
  priceMaxInput.value = String(price.value[1])
  const target = e.target as HTMLInputElement
  // Position cursor at the end of the numeric value after focus
  // Use setTimeout to ensure DOM is updated after computed value changes
  setTimeout(() => {
    const numericValue = priceMaxInput.value
    if (target && target.setSelectionRange) {
      target.setSelectionRange(numericValue.length, numericValue.length)
    }
  }, 0)
}

const handlePriceMinBlur = () => {
  isPriceMinFocused.value = false

  // Check if user actually typed (input value differs from focus-time value)
  const inputNumericValue = parsePriceToNumber(
    parsePriceInput(priceMinInput.value)
  )
  const userTyped =
    priceMinAtFocus.value !== null &&
    inputNumericValue !== priceMinAtFocus.value

  if (userTyped) {
    // User typed something - parse and validate the input value
    const validatedValue = processPriceMinInput(
      priceMinInput.value,
      price.value[1]
    )
    price.value[0] = validatedValue
    priceMinInput.value = String(validatedValue)
    emit('updated', { priceMin: validatedValue, priceMax: price.value[1] })
  } else {
    // User didn't type - sync input with current price.value (may have been updated externally)
    priceMinInput.value = String(price.value[0])
    // Don't emit update if value hasn't changed
  }

  priceMinAtFocus.value = null
}

const handlePriceMaxBlur = () => {
  isPriceMaxFocused.value = false

  // Check if user actually typed (input value differs from focus-time value)
  const inputNumericValue = parsePriceToNumber(
    parsePriceInput(priceMaxInput.value)
  )
  const userTyped =
    priceMaxAtFocus.value !== null &&
    inputNumericValue !== priceMaxAtFocus.value

  if (userTyped) {
    // User typed something - parse and validate the input value
    const validatedValue = processPriceMaxInput(
      priceMaxInput.value,
      price.value[0]
    )
    price.value[1] = validatedValue
    priceMaxInput.value = String(validatedValue)
    emit('updated', { priceMin: price.value[0], priceMax: validatedValue })
  } else {
    // User didn't type - sync input with current price.value (may have been updated externally)
    priceMaxInput.value = String(price.value[1])
    // Don't emit update if value hasn't changed
  }

  priceMaxAtFocus.value = null
}

const i18n = useI18n()
const availableLanguages = fnGetAvailableLanguages(i18n)
const price = ref([props.priceMin ?? PRICE_MIN, props.priceMax ?? PRICE_MAX])

// Local refs to store input values while typing (allows intermediate invalid values)
const priceMinInput = ref(String(props.priceMin ?? PRICE_MIN))
const priceMaxInput = ref(String(props.priceMax ?? PRICE_MAX))

const priceMinDisplay = computed({
  get: () => {
    // When focused, show raw input value (allows intermediate invalid values)
    // When not focused, show formatted value with euro sign
    if (isPriceMinFocused.value) {
      return priceMinInput.value
    }
    return `${price.value[0]} €`
  },
  set: (value: string) => {
    // Store the raw input value while typing (don't validate yet)
    priceMinInput.value = parsePriceInput(value)
  },
})

const priceMaxDisplay = computed({
  get: () => {
    // When focused, show raw input value (allows intermediate invalid values)
    // When not focused, show formatted value with euro sign
    if (isPriceMaxFocused.value) {
      return priceMaxInput.value
    }
    return `${price.value[1]} €`
  },
  set: (value: string) => {
    // Store the raw input value while typing (don't validate yet)
    priceMaxInput.value = parsePriceInput(value)
  },
})
</script>

<style lang="postcss" scoped>
.filter-column {
  flex: 1 1 100%;
}

.label {
  @apply h-9 font-medium text-black flex items-center;
}

.chip-filter {
  @apply m-0;

  &:not(.chip--selected) {
    @apply hover:border-teal-900 hover:bg-white hover:text-gray-700;
  }
}

.price-slider {
  :deep(.tooltip-text--1) {
    left: 0;
  }

  :deep(.tooltip-text--2) {
    right: 0;
  }
}
</style>
